import {
  badCommentOptions,
  addComment,
  editComment,
  cancelComment,
} from "@/aips/canvas/aiCanvasApi.js";
import { getGuideState, markGuideSeen } from "@/utils/userGuideStorage.js";
import {
  getLikeIconBase64,
  getDislikeIconBase64,
  getLikeIconActiveBase64,
  getDislikeIconActiveBase64,
} from "@/utils/svgToBase64.js";

export default {
  data() {
    return {
      // 反馈截图相关
      feedbackFileList: [],
      feedbackPreviewImage: "",
      feedbackUploadPreviewVisible: false,
      // 记录消息的点赞/点踩状态与反馈内容
      messageFeedbackStates: {},
      // 反馈弹窗相关
      feedbackModalVisible: false,
      feedbackTargetMessageId: null,
      feedbackTargetButtons: null,
      // 反馈截图上传中状态
      feedbackUploading: false,
      // 点赞/取消点赞节流用的时间戳
      likeActionLastTime: 0,
      // 反馈表单
      feedbackForm: {
        reasons: [],
        otherText: "",
        screenshotName: "",
      },
      // 差评原因选项，从后端 badCommentOptions 接口动态加载
      // 结构示例：[{ code: string, label: string }]
      feedbackReasons: [],
    };
  },
  created() {
    // 动态拉取差评原因选项
    badCommentOptions().then((res) => {
      if (!res) return;
      const ok = res.code === 200;
      if (ok && Array.isArray(res.data)) {
        this.feedbackReasons = res.data;
      }
    });
  },
  methods: {
    // ------ 报告顶部反馈组件：生成 HTML，只展示一次 ------
    generateFeedbackWidget(messageId) {
      const guideKey = "ai_report_feedback";
      const state = getGuideState(guideKey);

      // 已经展示过，则不再生成
      if (state && state.hasSeen) {
        return "";
      }

      // 第一次生成时标记为已查看
      markGuideSeen(guideKey);

      const likeIconSrc = getLikeIconBase64();
      const dislikeIconSrc = getDislikeIconBase64();

      return `
        <div class="ai-report-feedback-widget" data-message-id="${messageId}">
          <div class="ai-report-feedback-header">
            <div class="ai-report-feedback-question">您好，这份案件分析报告对您有帮助吗？</div>
            <div class="ai-report-feedback-subtitle">您的反馈将用于持续优化报告质量</div>
          </div>
          <div class="ai-report-feedback-options">
            <button class="ai-report-feedback-option" type="button" data-feedback="like">
              <img class="ai-report-feedback-icon" src="${likeIconSrc}" alt="有帮助" />
              <span>有帮助，信息清晰</span>
            </button>
            <button class="ai-report-feedback-option" type="button" data-feedback="dislike">
              <img class="ai-report-feedback-icon" src="${dislikeIconSrc}" alt="不太理想" />
              <span>不太理想，存在一些问题</span>
            </button>
          </div>
        </div>
      `;
    },

    // 绑定报告顶部反馈组件的交互事件
    bindFeedbackWidgetEvents(messageId) {
      const widget = document.querySelector(
        `.ai-report-feedback-widget[data-message-id="${messageId}"]`
      );
      if (!widget) return;

      const options = widget.querySelectorAll(".ai-report-feedback-option");

      const likeIconNormal = getLikeIconBase64();
      const dislikeIconNormal = getDislikeIconBase64();
      const likeIconActive = getLikeIconActiveBase64();
      const dislikeIconActive = getDislikeIconActiveBase64();

      const likeButton = widget.querySelector(
        '.ai-report-feedback-option[data-feedback="like"]'
      );
      const dislikeButton = widget.querySelector(
        '.ai-report-feedback-option[data-feedback="dislike"]'
      );
      const likeImg =
        likeButton && likeButton.querySelector(".ai-report-feedback-icon");
      const dislikeImg =
        dislikeButton &&
        dislikeButton.querySelector(".ai-report-feedback-icon");

      options.forEach((option) => {
        option.addEventListener("click", (e) => {
          const feedbackType = option.dataset.feedback;
          const msgId = widget.dataset.messageId;

          const wasSelected = option.classList.contains("selected");

          // 先移除所有选中状态
          options.forEach((opt) => opt.classList.remove("selected"));

          // 默认恢复为灰色图标
          if (likeImg) likeImg.src = likeIconNormal;
          if (dislikeImg) dislikeImg.src = dislikeIconNormal;

          if (feedbackType === "like") {
            // 再次点击已选中的“有帮助”视为取消点赞
            if (!wasSelected) {
              option.classList.add("selected");
              if (likeImg) likeImg.src = likeIconActive;
            }

            this.handleReportFeedback(msgId, "like");
          } else if (feedbackType === "dislike") {
            // 点踩：仅弹出反馈弹窗，不在顶部组件里呈现选中态
            // 顶部这块区域在差评提交成功后会被移除
            if (typeof this.openFeedbackModal === "function") {
              this.openFeedbackModal(msgId, null);
            } else {
              this.feedbackTargetMessageId = msgId;
              this.feedbackTargetButtons = null;
              this.feedbackModalVisible = true;
            }
          }
        });
      });
    },

    // 处理报告顶部组件的反馈（目前只使用点赞）
    handleReportFeedback(messageId, type) {
      // 报告组件只使用点赞，复用 submitLike 逻辑
      if (type === "like" && typeof this.submitLike === "function") {
        this.submitLike(messageId).then((result) => {
          // 点赞或取消点赞成功后，根据最新的 messageFeedbackStates 刷新消息上的点赞/点踩图标
          if (result === "like" || result === "cancel_like") {
            if (typeof this.addMessageActionIcons === "function") {
              // 重新渲染所有消息的操作按钮（包括目标消息的点赞图标）
              this.$nextTick(() => {
                this.addMessageActionIcons();
              });
            }
          }
        });
        return;
      }

      // 兜底：如果没有 submitLike，则仅记录状态
      if (!this.messageFeedbackStates) {
        this.messageFeedbackStates = {};
      }
      this.messageFeedbackStates[messageId] = {
        status: type,
        timestamp: new Date().toISOString(),
      };
    },

    // ------ 纯业务逻辑：处理点赞/取消点赞，只更新后端和 messageFeedbackStates，不操作 DOM ------
    async submitLike(messageId) {
      // 调试：打印实例信息，确认是哪个 AIChat 实例
      console.log("=== 点赞调试 ===");
      console.log("aiCanvasRobot:", this.aiCanvasRobot);
      console.log("ai_Model_Types:", this.ai_Model_Types);
      console.log("实例 _uid:", this._uid);
      console.log("================");

      if (!messageId) return null;

      // 简单节流：1 秒内只允许一次点赞/取消点赞
      const now = Date.now();
      if (this.likeActionLastTime && now - this.likeActionLastTime < 1000) {
        if (this.$notify) {
          this.$tbMessage({
            text: "操作太频繁，请稍后再试",
            type: "warning",
            duration: 1200,
          })
        }
        return "throttled";
      }
      this.likeActionLastTime = now;

      const current = this.messageFeedbackStates[messageId];

      // 已点踩：禁止再点赞
      if (current && current.status === "dislike") {
        return "blocked";
      }

      const targetMsg = (this.messages || []).find(
        (m) => m && m._id === messageId
      );
      const taskId = targetMsg && targetMsg.taskId;
      if (!taskId) {
        console.warn("未找到对应消息的 taskId，无法提交点赞/取消点赞", {
          messageId,
          targetMsg,
        });
        return null;
      }

      try {
        // 当前是点赞状态 -> 取消点赞
        if (current && current.status === "like") {
          // ADDRESS_ANALYSIS 场景使用 taskId，其他场景使用 messageId 字段
          const cancelPayload =
            this.ai_Model_Types === "ADDRESS_ANALYSIS"
              ? {
                  taskId,
                  aiModelType: this.ai_Model_Types,
                }
              : {
                  messageId: taskId,
                  aiModelType: this.ai_Model_Types,
                };

          const res = await cancelComment(cancelPayload);
          const ok = res && res.code === 200;
          if (!ok) {
            return "error";
          }

          const { [messageId]: removed, ...rest } = this.messageFeedbackStates;
          this.messageFeedbackStates = { ...rest };
          return "cancel_like";
        }

        // 未评价 -> 点赞
        // 根据 ai_Model_Types 区分入口（ADDRESS_ANALYSIS=AI画布, 其他=全局小机器人等）
        const addPayload =
          this.ai_Model_Types === "ADDRESS_ANALYSIS"
            ? {
                taskId,
                evaluateResult: "GOOD",
                aiModelType: this.ai_Model_Types,
              }
            : {
                messageId: taskId,
                evaluateResult: "GOOD",
                aiModelType: this.ai_Model_Types,
              };

        const res = await addComment(addPayload);
        const ok = res && res.code === 200;
        if (!ok) {
          return "error";
        }

        this.messageFeedbackStates = {
          ...this.messageFeedbackStates,
          [messageId]: { status: "like" },
        };
        return "like";
      } catch (err) {
        console.error("提交点赞/取消点赞失败", err);
        return "error";
      }
    },

    // 点赞图标点击处理（底部操作区）
    handleLikeFeedback(messageId, likeBtn, dislikeBtn) {
      if (!messageId) return;

      this.submitLike(messageId).then((result) => {
        if (result === "like") {
          likeBtn.classList.add("active");
          if (dislikeBtn) dislikeBtn.classList.remove("active");
        } else if (result === "cancel_like") {
          likeBtn.classList.remove("active");
          if (dislikeBtn) dislikeBtn.classList.remove("active");
        }
      });
    },

    // 点踩图标点击处理（底部操作区）
    handleDislikeFeedback(messageId, dislikeBtn, likeBtn) {
      if (!messageId) return;
      if (typeof this.openFeedbackModal === "function") {
        this.openFeedbackModal(messageId, { dislikeBtn, likeBtn });
      }
    },

    // ------ 反馈弹窗逻辑（与截图上传协作） ------
    openFeedbackModal(messageId, buttons) {
      // 兼容来自多个 mixin 的调用
      this.feedbackTargetMessageId = messageId;
      this.feedbackTargetButtons = buttons;
      // 每次打开反馈弹窗时重新获取 OSS 授权，避免 token 过期
      if (typeof this.getOssToken === "function") {
        this.getOssToken();
      }
      this.resetFeedbackForm();
    },
    resetFeedbackForm() {
      this.feedbackForm = {
        reasons: [],
        otherText: "",
        screenshotName: "",
      };
      if (this.$refs && this.$refs.feedbackFileInput) {
        this.$refs.feedbackFileInput.value = "";
      }
      this.feedbackUploading = false;
      this.feedbackModalVisible = true;
    },
    toggleFeedbackReason(reason) {
      const code = typeof reason === "string" ? reason : reason.code;
      if (!code) return;

      const exists = this.feedbackForm.reasons.includes(code);
      if (exists) {
        this.feedbackForm.reasons = this.feedbackForm.reasons.filter(
          (r) => r !== code
        );
      } else {
        this.feedbackForm.reasons = [...this.feedbackForm.reasons, code];
      }
    },
    // 反馈：上传列表变更
    handleUploadChange(file, fileList) {
      this.feedbackFileList = fileList;
    },
    // 反馈：移除图片
    handleRemove(file, fileList) {
      this.feedbackFileList = fileList;
      if (!fileList || fileList.length === 0) {
        this.feedbackForm.screenshotName = "";
      }
    },
    // 反馈：预览
    handlePictureCardPreview(file) {
      const url =
        file.url ||
        (file.response && file.response.url) ||
        (file.raw && (file.raw.url || URL.createObjectURL(file.raw))) ||
        "";
      if (!url) return;
      this.feedbackPreviewImage = url;
      this.feedbackUploadPreviewVisible = true;
    },
    onFeedbackFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) {
        this.feedbackForm.screenshotName = file.name;
      } else {
        this.feedbackForm.screenshotName = "";
      }
    },
    // 反馈：关闭弹窗
    closeFeedbackModal() {
      this.feedbackModalVisible = false;
      this.feedbackForm = {
        reasons: [],
        otherText: "",
        screenshotName: "",
      };
      this.feedbackFileList = [];
      this.feedbackPreviewImage = "";
      this.feedbackUploadPreviewVisible = false;
      this.feedbackUploading = false;
    },
    // 提交差评反馈
    confirmFeedback() {
      // 如果截图仍在上传中，阻止提交
      if (this.feedbackUploading) {
        this.$tbMessage({
          text: "图片正在上传，请稍后再确认",
          type: "warning",
          duration: 1500,
        })
        return;
      }
      const hasReason = this.feedbackForm.reasons.length > 0;
      const hasText = this.feedbackForm.otherText.trim().length > 0;
      if (!hasReason && !hasText) {
        this.$tbMessage({
          text: "请至少选择一个问题或填写描述",
          type: "warning",
          duration: 1500,
        })
        return;
      }
      if (!this.feedbackTargetMessageId) {
        this.closeFeedbackModal();
        return;
      }
      // 1. 根据反馈目标消息 ID 找到对应消息，获取 taskId
      const messageId = this.feedbackTargetMessageId;
      const targetMsg = (this.messages || []).find(
        (m) => m && m._id === messageId
      );
      const taskId = targetMsg && targetMsg.taskId;

      if (!taskId) {
        console.warn("未找到对应消息的 taskId，无法提交差评", {
          messageId,
          targetMsg,
        });
        return;
      }

      // 2. 组装 problemTypes（使用选中的差评原因 code）
      const problemTypes = [...this.feedbackForm.reasons];

      // 3. 组装 files 数组：使用刚上传的截图
      let files = [];
      if (this.feedbackForm.screenshotName) {
        const firstFile =
          (this.feedbackFileList && this.feedbackFileList[0]) || null;
        files = [
          {
            filename: this.feedbackForm.screenshotName,
            originalFilename: firstFile && firstFile.name ? firstFile.name : "",
          },
        ];
      }

      // 4. 根据是否已好评决定调用 addComment 还是 editComment
      const currentState = this.messageFeedbackStates[messageId];
      const wasLiked = currentState && currentState.status === "like";
      // 根据 ai_Model_Types 区分入口（ADDRESS_ANALYSIS=AI画布, 其他=全局小机器人等）
      const payload =
        this.ai_Model_Types === "ADDRESS_ANALYSIS"
          ? {
              taskId,
              evaluateResult: "BAD",
              files,
              problemTypes,
              aiModelType: this.ai_Model_Types,
            }
          : {
              messageId: taskId,
              evaluateResult: "BAD",
              files,
              problemTypes,
              aiModelType: this.ai_Model_Types,
            };

      const requestFn = wasLiked ? editComment : addComment;

      requestFn(payload)
        .then((res) => {
          const ok = res && res.code === 200;
          if (!ok) {
            throw new Error("提交差评失败");
          }

          // 更新状态
          this.messageFeedbackStates = {
            ...this.messageFeedbackStates,
            [this.feedbackTargetMessageId]: {
              status: "dislike",
              reasons: [...this.feedbackForm.reasons],
              otherText: this.feedbackForm.otherText.trim(),
              screenshotName: this.feedbackForm.screenshotName,
            },
          };

          // 更新按钮状态
          if (this.feedbackTargetButtons?.dislikeBtn) {
            this.feedbackTargetButtons.dislikeBtn.classList.add("active");
          }
          if (this.feedbackTargetButtons?.likeBtn) {
            this.feedbackTargetButtons.likeBtn.classList.remove("active");
          }

          // 用户完成差评后，移除报告顶部的反馈组件区域（只针对当前消息）
          try {
            const widget = document.querySelector(
              `.ai-report-feedback-widget[data-message-id="${messageId}"]`
            );
            if (widget && widget.parentNode) {
              widget.parentNode.removeChild(widget);
            }
          } catch (e) {
            // DOM 查询异常直接忽略，不影响核心反馈提交流程
          }

          this.$tbMessage({
            text: "反馈已提交，感谢您的帮助！",
            type: "success",
            duration: 1200,
          });

          this.closeFeedbackModal();

          // 重新渲染操作按钮以应用隐藏/禁用逻辑
          this.$nextTick(() => {
            if (typeof this.addMessageActionIcons === "function") {
              this.addMessageActionIcons();
            }
          });
        })
        .catch((err) => {
          console.error("提交差评失败", err);
          this.$tbMessage({
            text: "提交反馈失败，请稍后重试",
            type: "error",
            duration: 1500,
          })
        });
    },
  },
};
